import type { Brand, BrandAsset, BrandImage } from "./brand.model";

// Tags are free text, so they're shown exactly as stored — an untagged asset
// falls back to its filename rather than inventing a name for it.
export const assetDisplayName = (tag: string | null, name: string | null) => {
  if (tag) return tag;
  if (!name) return "Logo";
  const path = name.split("?")[0];
  return decodeURIComponent(path.substring(path.lastIndexOf("/") + 1)) || "Logo";
};

// Presigned URLs differ per call (signature query), so identity is the S3 key alone.
export const storageKey = (url: string | null) => {
  if (!url) return "";
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

/**
 * Every logo on file, default first. Falls back to `logo_url` when it has no
 * asset row behind it, so brands migrated or uploaded before BrandAsset existed
 * still offer their logo rather than nothing.
 */
export function brandLogoImages(brand: Brand | null): BrandImage[] {
  if (!brand) return [];
  const defaultKey = storageKey(brand.logo_url);
  const assets: BrandAsset[] = brand.assets ?? [];

  const images = assets
    .filter((asset) => asset.url)
    .map((asset) => ({
      name: assetDisplayName(asset.tag, asset.name),
      url: asset.url as string,
      isDefault: !!defaultKey && storageKey(asset.url) === defaultKey,
    }));

  if (brand.logo_url && !images.some((image) => image.isDefault)) {
    images.unshift({ name: "Logo", url: brand.logo_url, isDefault: true });
  }

  return [...images.filter((i) => i.isDefault), ...images.filter((i) => !i.isDefault)]
    .map(({ name, url }) => ({ name, url }));
}
